import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Library, Loader2, X } from 'lucide-react';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/** Task output types a Session Hub can share (the deliverable file types). */
export const HUB_SHAREABLE_OUTPUT_TYPES = new Set(['html_artifact', 'pdf_document', 'video', 'image']);

interface Props {
  projectId?: string;
  sessionId: string;
  taskId: string;
  outputIndex: number;
  outputType: string;
}

interface HubSummary {
  hubId: string;
  title: string;
  kind: string;
  status: string;
  expiresAt: string;
}

type Audience = 'session' | 'hub';

interface Share {
  hubId: string;
  hubTitle: string;
  itemId: string;
  outputIndex: number;
  audience: Audience;
}

export const AUDIENCE_LABEL: Record<Audience, string> = {
  session: "This session's attendees and the sponsor",
  hub: 'Everyone on the hub',
};

const errorOf = (e: any) => e?.response?.data?.error ?? e?.message ?? 'Request failed';

/**
 * Shares one task output file on one of the project's Session Hubs, or stops
 * sharing it. Shown only to people who can set up hubs.
 */
export function PublishToHubButton({ projectId, sessionId, taskId, outputIndex, outputType }: Props) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [hubs, setHubs] = useState<HubSummary[] | null>(null);
  const [shares, setShares] = useState<Share[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audience, setAudience] = useState<Audience>('session');
  const panel = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [h, s] = await Promise.all([
        api.get(`/api/projects/${projectId}/session-hubs`),
        api.get(`/api/projects/${projectId}/session-hub-shares`, { params: { sessionId, taskId } }),
      ]);
      setHubs((h.data.hubs as HubSummary[]).filter((x) => x.kind === 'library'));
      setShares((s.data.shares as Share[]).filter((x) => x.outputIndex === outputIndex));
    } catch (e) {
      setError(errorOf(e));
      setHubs([]);
    }
  }, [projectId, sessionId, taskId, outputIndex]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (panel.current && !panel.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!projectId || !HUB_SHAREABLE_OUTPUT_TYPES.has(outputType) || !can('SessionHub::Manage')) return null;

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(errorOf(e));
    } finally {
      setBusy(null);
    }
  }

  const publish = (hubId: string) => run(hubId, () =>
    api.post(`/api/projects/${projectId}/session-hubs/${hubId}/items`, { sessionId, taskId, outputIndex, audience }));
  const unpublish = (share: Share) => run(share.hubId, () =>
    api.delete(`/api/projects/${projectId}/session-hubs/${share.hubId}/items/${encodeURIComponent(share.itemId)}`));

  return (
    <div className="relative" ref={panel} onClick={(e) => e.stopPropagation()}>
      <button
        type="button" onClick={() => setOpen(!open)} title="Share this file with client contacts on a Session Hub"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors text-slate-500 hover:text-primary hover:bg-primary/5 border-slate-200 hover:border-primary/30"
      >
        <Library size={14} /> Publish to Session Hub
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-[#e2e8f0] rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
            <p className="text-sm font-semibold text-navy">Share on a Session Hub</p>
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
          {error && <p className="px-4 py-2 text-xs text-red-700 bg-red-50 border-b border-red-100">{error}</p>}
          <div className="px-4 py-3 border-b border-[#e2e8f0]">
            <label htmlFor={`aud-${taskId}-${outputIndex}`} className="block text-xs font-semibold text-slate-500 mb-1">Who can see it</label>
            <select
              id={`aud-${taskId}-${outputIndex}`} value={audience} onChange={(e) => setAudience(e.target.value as Audience)}
              className="w-full border border-[#e2e8f0] rounded-lg h-8 px-2 text-xs text-navy bg-white"
            >
              {(Object.keys(AUDIENCE_LABEL) as Audience[]).map((a) => <option key={a} value={a}>{AUDIENCE_LABEL[a]}</option>)}
            </select>
          </div>
          {hubs === null ? (
            <div className="flex justify-center py-6 text-[#64748b]"><Loader2 size={18} className="animate-spin" /></div>
          ) : hubs.length === 0 ? (
            <p className="px-4 py-4 text-xs text-slate-500">This project has no Session Hub yet. Create one in the project's Session Hub tab.</p>
          ) : (
            <ul className="divide-y divide-[#e2e8f0] max-h-72 overflow-y-auto">
              {hubs.map((h) => {
                const share = shares.find((s) => s.hubId === h.hubId);
                const closed = h.status !== 'active' || Date.parse(h.expiresAt) <= Date.now();
                return (
                  <li key={h.hubId} className="px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-navy truncate">{h.title}</p>
                      <p className="text-xs text-slate-400">{closed ? 'Closed' : share ? `Shared with ${share.audience === 'hub' ? 'everyone' : "this session's attendees"}` : 'Not shared'}</p>
                    </div>
                    {share ? (
                      <button
                        type="button" disabled={busy === h.hubId} onClick={() => unpublish(share)}
                        className="flex items-center gap-1.5 rounded-lg h-8 px-3 border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                      >
                        {busy === h.hubId ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} className="text-green-600" />} Unshare
                      </button>
                    ) : (
                      <button
                        type="button" disabled={busy === h.hubId || closed} onClick={() => publish(h.hubId)}
                        className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark text-white text-xs font-bold disabled:opacity-60"
                      >
                        {busy === h.hubId ? <Loader2 size={12} className="animate-spin" /> : null} Share
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
